import React, { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { ScanLine, X, Keyboard, RefreshCcw, PackagePlus, PackageMinus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function BarcodeScanner() {
  const scannerRef = useRef(null);
  const qrRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [item, setItem] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [action, setAction] = useState({ type: null, qty: 1, open: false });

  const start = async () => {
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      if (!qrRef.current) qrRef.current = new Html5Qrcode("operon-reader");
      await qrRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 240 },
        async (decoded) => {
          if (decoded === result) return;
          setResult(decoded);
          try {
            const { data } = await api.get("/inventory/lookup", { params: { code: decoded } });
            setItem(data);
            toast.success(`Found: ${data.name}`);
            if (navigator?.vibrate) navigator.vibrate(80);
          } catch (e) {
            toast.error(`No item with code ${decoded}`);
            setItem(null);
          }
        },
        () => {}
      );
      setRunning(true);
    } catch (e) {
      toast.error(extractErr(e, "Camera access denied"));
    }
  };

  const stop = async () => {
    try {
      if (qrRef.current && qrRef.current.isScanning) {
        await qrRef.current.stop();
      }
    } catch {}
    setRunning(false);
  };

  useEffect(() => {
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookupManual = async () => {
    if (!manualCode) return;
    setManualOpen(false);
    setResult(manualCode);
    try {
      const { data } = await api.get("/inventory/lookup", { params: { code: manualCode } });
      setItem(data);
      toast.success(`Found: ${data.name}`);
    } catch (e) {
      toast.error(`No item with code ${manualCode}`);
      setItem(null);
    }
    setManualCode("");
  };

  const performAction = async () => {
    if (!item || !action.type) return;
    const qty = Number(action.qty);
    if (qty <= 0) { toast.error("Quantity must be > 0"); return; }
    try {
      if (action.type === "intake") {
        await api.post("/goods/intake", { item_id: item.id, quantity: qty, note: "Scanned intake" });
        toast.success(`Added ${qty} ${item.unit || ""} to ${item.sku}`);
      } else {
        await api.post("/goods/outtake", { item_id: item.id, quantity: qty, reason: "usage", note: "Scanned outtake" });
        toast.success(`Removed ${qty} ${item.unit || ""} from ${item.sku}`);
      }
      setAction({ type: null, qty: 1, open: false });
      // Refresh item info
      const { data } = await api.get(`/inventory/${item.id}`);
      setItem(data);
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  return (
    <AppShell title="Barcode / QR Scanner" subtitle="Point camera at barcode or QR. Manual entry available.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold flex items-center gap-2"><ScanLine className="w-4 h-4 text-primary" /> Camera</div>
            <div className="flex items-center gap-2">
              {running ? (
                <Button size="sm" variant="ghost" onClick={stop} data-testid="scanner-stop-button"><X className="w-4 h-4 mr-1" />Stop</Button>
              ) : (
                <Button size="sm" onClick={start} data-testid="scanner-start-button"><ScanLine className="w-4 h-4 mr-1" />Start</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setManualOpen(true)} data-testid="scanner-manual-button"><Keyboard className="w-4 h-4 mr-1" />Manual</Button>
            </div>
          </div>
          <div className="rounded-xl bg-black aspect-video flex items-center justify-center relative overflow-hidden">
            <div id="operon-reader" ref={scannerRef} className="w-full h-full" />
            {!running && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 text-center px-6">
                <ScanLine className="w-8 h-8 mb-2 text-primary" />
                <div className="font-heading text-lg">Tap Start to scan</div>
                <div className="text-xs text-white/60 mt-1">Operon never activates the camera until you tap start.</div>
                <Button onClick={start} className="mt-4"><ScanLine className="w-4 h-4 mr-1" />Start camera</Button>
              </div>
            )}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white/80 pointer-events-none">
              <div className="text-xs uppercase tracking-wider opacity-70">Scanning…</div>
              {result && <div className="text-xs font-mono bg-white/10 px-2 py-1 rounded">{result}</div>}
            </div>
            {running && (
              <div className="absolute inset-16 border-2 border-primary/80 rounded-2xl operon-reticle-pulse pointer-events-none" />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4" data-testid="barcode-scanner-result">
          <div className="flex items-center justify-between">
            <div className="font-heading font-semibold">Last scan</div>
            {result && <Button size="sm" variant="ghost" onClick={() => { setResult(null); setItem(null); }}><RefreshCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>}
          </div>
          {!result ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Scan a code or enter manually to look up an item.</div>
          ) : !item ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Code <span className="font-mono">{result}</span> not matched to any item.</div>
          ) : (
            <div>
              <div className="flex items-center gap-3">
                {item.image_base64 && <img src={item.image_base64} alt="" className="w-14 h-14 rounded-lg object-cover border border-border" />}
                <div>
                  <div className="font-heading text-lg font-semibold">{item.name}</div>
                  <div className="text-xs font-mono text-muted-foreground">{item.sku}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                <Info label="Qty" value={`${item.quantity} ${item.unit || ""}`} />
                <Info label="Min" value={item.min_stock} />
                <Info label="Category" value={item.category} />
                <Info label="Location" value={item.location || "—"} />
                <Info label="Warehouse" value={item.warehouse || "—"} />
                <Info label="Supplier" value={item.supplier || "—"} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button onClick={() => setAction({ type: "intake", qty: 1, open: true })} data-testid="scanner-intake-button"><PackagePlus className="w-4 h-4 mr-1.5" />Intake</Button>
                <Button variant="secondary" onClick={() => setAction({ type: "outtake", qty: 1, open: true })} data-testid="scanner-outtake-button"><PackageMinus className="w-4 h-4 mr-1.5" />Outtake</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter barcode / SKU</DialogTitle>
            <DialogDescription>Type or paste a barcode/SKU to look it up in your inventory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Code</Label>
            <Input autoFocus value={manualCode} onChange={(e) => setManualCode(e.target.value)} className="font-mono" data-testid="scanner-manual-input" onKeyDown={(e) => e.key === "Enter" && lookupManual()} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button>
            <Button onClick={lookupManual} data-testid="scanner-manual-lookup-button">Look up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={action.open} onOpenChange={(v) => setAction((a) => ({ ...a, open: v }))}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="capitalize">{action.type} — {item?.sku}</DialogTitle>
            <DialogDescription>Adjust the quantity and confirm to record the movement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Quantity</Label>
            <Input autoFocus type="number" min="0" step="any" value={action.qty} onChange={(e) => setAction((a) => ({ ...a, qty: e.target.value }))} data-testid="scanner-action-qty-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAction({ type: null, qty: 1, open: false })}>Cancel</Button>
            <Button onClick={performAction} data-testid="scanner-action-confirm-button">Confirm {action.type}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}
